import React, { useState } from "react";
import { Sparkles, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";

const AI_BADGE = (
  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-[#F26A21]/20 border border-[#F26A21]/40 text-[#F26A21] uppercase font-bold tracking-wider">
    <Sparkles className="w-2.5 h-2.5 inline mr-0.5" aria-hidden="true" />IA
  </span>
);
const CONFIRM_BADGE = (
  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-800 uppercase font-bold tracking-wider">
    <AlertCircle className="w-2.5 h-2.5 inline mr-0.5" aria-hidden="true" />Confirmar
  </span>
);

const MODALITIES = ["online", "presencial", "blended"];
const SESSION_FORMATS = ["en_vivo", "grabado", "mixto"];
const REGIONS = ["LATAM", "Norteamérica", "Europa", "Asia", "Oceanía", "África"];
const PROGRAM_TYPES = ["curso", "taller", "webinar", "programa", "diplomado", "certificacion", "masterclass"];
const LANGUAGES = ["es-LATAM", "es-ES", "en", "otro"];
const EMPLOYEE_COUNTS = ["1-10", "11-50", "51-200", "201-1000", "+1000"];
const PERIODICITIES = ["semanal", "quincenal", "mensual"];
const ENDORSEMENTS_OPTS = ["DCC", "IADA", "ITAM", "Universidad Panamericana", "La Salle", "Aval propio"];
const THEMATIC_OPTS = ["Management", "Finanzas", "Riesgo", "Legal", "Tecnología", "Ventas", "Marketing", "Ecommerce", "Soft Skills", "Sustentabilidad"];
const MATERIALS_OPTS = ["Presentaciones", "Infografías", "Videos animados", "Íconos", "eBook", "Casos", "Simuladores", "Templates"];
const RESOURCES_OPTS = ["Gamification", "Formularios en tiempo real", "Interacción entre participantes", "Simulaciones", "Casos de estudio", "Mentorías 1:1"];
const EVALUATION_OPTS = ["Asistencia", "Participación", "Trabajos", "Exámenes", "Proyecto final integrador"];
const TECH_OPTS = ["Landing de inscripciones", "Streaming interno", "Streaming externo", "Calendarización", "CRM", "Grabación de sesiones"];
const CHANNEL_OPTS = ["Email", "WhatsApp", "SMS", "Portal", "App móvil"];
const PROMO_OPTS = ["Banners RRSS", "Flyer", "Brochure", "Landing", "HTML mailing", "QR", "Botón de pago"];

const CONFIRM_KEYS = ["current_lms", "internal_training_staff", "team_for_promotion", "team_for_enrollment", "team_for_coordination"];

export default function BriefReviewStep({ brief, setBrief, programs, onBack, onNext, onRegenerate, regenerateEnabled }) {
  const [open, setOpen] = useState("A");
  const total = countTotals(brief);

  return (
    <div className="space-y-4" data-testid="brief-review-step">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">Paso 2</p>
          <h2 className="mt-1 text-2xl font-bold text-[#0B0B0B] font-display">Revisamos juntos el brief</h2>
          <p className="mt-1 text-sm text-gray-600">Todo lo generó la IA — edita lo que quieras y confirma.</p>
        </div>
        {regenerateEnabled && (
          <button onClick={onRegenerate} data-testid="regenerate-btn"
            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-[#0B0B0B] hover:bg-black/5">
            Regenerar propuesta
          </button>
        )}
      </div>

      <div className="p-3 rounded-md bg-[#F9FAFB] border border-gray-200 flex flex-wrap gap-3 text-xs">
        <span><span className="text-[#F26A21] font-semibold">{total.ia}</span> campos generados por IA</span>
        <span className="text-gray-400">·</span>
        <span><span className="text-amber-700 font-semibold">{total.pending}</span> pendientes de confirmar</span>
      </div>

      <Section id="A" title="A · Conoce tu organización (KYC)" open={open} setOpen={setOpen}>
        <KycSection brief={brief} setBrief={setBrief} />
      </Section>
      <Section id="B" title="B · Estructura del training" open={open} setOpen={setOpen}>
        <StructureSection brief={brief} setBrief={setBrief} />
      </Section>
      <Section id="C" title="C · Logística" open={open} setOpen={setOpen}>
        <LogisticsSection brief={brief} setBrief={setBrief} />
      </Section>
      <Section id="D" title="D · Contenido" open={open} setOpen={setOpen}>
        <ContentSection brief={brief} setBrief={setBrief} programs={programs} />
      </Section>
      <Section id="E" title="E · Requerimientos operativos" open={open} setOpen={setOpen}>
        <OperationsSection brief={brief} setBrief={setBrief} />
      </Section>

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="text-sm text-gray-600 hover:text-[#0B0B0B]">← Regresar</button>
        <button onClick={onNext} disabled={total.pending > 0}
          data-testid="brief-next-btn"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          Continuar a contacto
        </button>
      </div>
    </div>
  );
}

function Section({ id, title, open, setOpen, children }) {
  const isOpen = open === id;
  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid={`brief-section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen(isOpen ? null : id)}
        aria-expanded={isOpen}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#F9FAFB]"
      >
        <span className="text-sm font-semibold text-[#0B0B0B]">{title}</span>
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {isOpen && <div className="px-4 pb-4 pt-2 space-y-3">{children}</div>}
    </div>
  );
}

function countTotals(brief) {
  let ia = 0, pending = 0;
  const scan = (obj) => {
    for (const [k, v] of Object.entries(obj || {})) {
      if (v && typeof v === "object" && !Array.isArray(v)) scan(v);
      else if (Array.isArray(v)) { if (v.length > 0) ia += 1; }
      else if (v !== null && v !== undefined && v !== "") ia += 1;
    }
  };
  scan(brief);
  const rc = brief?.operations?.requires_confirmation || {};
  for (const k of CONFIRM_KEYS) if (rc[k] === null || rc[k] === undefined || rc[k] === "") pending += 1;
  return { ia, pending };
}

// ---------- SECTIONS ----------
function KycSection({ brief, setBrief }) {
  const k = brief.kyc || {};
  const upd = (patch) => setBrief({ ...brief, kyc: { ...k, ...patch } });
  return (
    <>
      <Textarea label={<>Descripción de la empresa {AI_BADGE}</>} val={k.company_description || ""} on={(v) => upd({ company_description: v })} rows={3} testid="kyc-desc" />
      <Text label={<>Sector {AI_BADGE}</>} val={k.sector || ""} on={(v) => upd({ sector: v })} testid="kyc-sector" />
      <SelectRow label={<>Tamaño de empleados {AI_BADGE}</>} val={k.employee_count || ""} on={(v) => upd({ employee_count: v })} options={EMPLOYEE_COUNTS} testid="kyc-employees" />
      <Text label={<>Países (separados por coma) {AI_BADGE}</>} val={(k.countries || []).join(", ")} on={(v) => upd({ countries: v.split(",").map((x) => x.trim()).filter(Boolean) })} testid="kyc-countries" />
      <Text label={<>Certificaciones o NOMs {CONFIRM_BADGE}</>} val={k.certifications || ""} on={(v) => upd({ certifications: v })} placeholder="Ej: PLD, CNBV, CONDUSEF (opcional)" testid="kyc-certs" />
    </>
  );
}

function StructureSection({ brief, setBrief }) {
  const s = brief.structure || {};
  const upd = (patch) => setBrief({ ...brief, structure: { ...s, ...patch } });
  return (
    <>
      <Radio label={<>Modalidad {AI_BADGE}</>} val={s.modality || "blended"} on={(v) => upd({ modality: v })} options={MODALITIES} testid="struct-modality" />
      <Radio label={<>Formato de sesiones {AI_BADGE}</>} val={s.session_format || "en_vivo"} on={(v) => upd({ session_format: v })} options={SESSION_FORMATS} testid="struct-format" />
      <Radio label={<>Región {AI_BADGE}</>} val={s.region || "LATAM"} on={(v) => upd({ region: v })} options={REGIONS} testid="struct-region" />
      <Radio label={<>Tipo de programa {AI_BADGE}</>} val={s.program_type || "diplomado"} on={(v) => upd({ program_type: v })} options={PROGRAM_TYPES} testid="struct-progtype" />
      <Radio label={<>Idioma {AI_BADGE}</>} val={s.language || "es-LATAM"} on={(v) => upd({ language: v })} options={LANGUAGES} testid="struct-language" />
      <CheckList label={<>Aval académico {AI_BADGE}</>} vals={s.endorsements || ["DCC"]} on={(vals) => upd({ endorsements: vals })} options={ENDORSEMENTS_OPTS} testid="struct-endorsements" />
      {(s.endorsements || []).includes("IADA") && (
        <div className="mt-2 p-2 bg-[#F26A21]/10 border border-[#F26A21]/30 rounded text-[11px] text-[#0B0B0B]" data-testid="iada-cost-note">
          <span className="font-semibold">Nota:</span> El aval IADA incrementa el costo aproximado del programa un 30% sobre la base del catálogo.
        </div>
      )}
    </>
  );
}

function LogisticsSection({ brief, setBrief }) {
  const l = brief.logistics || {};
  const upd = (patch) => setBrief({ ...brief, logistics: { ...l, ...patch } });
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <TextNum label={<>Total asistentes {CONFIRM_BADGE}</>} val={l.attendees_total || 0} on={(v) => upd({ attendees_total: parseInt(v, 10) || 0 })} testid="log-total" />
      <TextNum label={<>Por grupo {AI_BADGE}</>} val={l.attendees_per_group || 0} on={(v) => upd({ attendees_per_group: parseInt(v, 10) || 0 })} testid="log-per-group" />
      <TextDate label={<>Fecha inicio {AI_BADGE}</>} val={l.start_date || ""} on={(v) => upd({ start_date: v })} testid="log-start" />
      <TextDate label={<>Fecha término {AI_BADGE}</>} val={l.end_date || ""} on={(v) => upd({ end_date: v })} testid="log-end" />
      <Text label={<>Horario preferente {AI_BADGE}</>} val={l.preferred_schedule || ""} on={(v) => upd({ preferred_schedule: v })} testid="log-schedule" />
      <TextNum label={<>Duración por sesión (h) {AI_BADGE}</>} val={l.session_duration_hours || 1} on={(v) => upd({ session_duration_hours: parseFloat(v) || 1 })} testid="log-session-h" />
      <Radio label={<>Periodicidad {AI_BADGE}</>} val={l.periodicity || "semanal"} on={(v) => upd({ periodicity: v })} options={PERIODICITIES} testid="log-periodicity" />
    </div>
  );
}

function ContentSection({ brief, setBrief, programs }) {
  const c = brief.content || {};
  const upd = (patch) => setBrief({ ...brief, content: { ...c, ...patch } });
  const suggested = c.suggested_program_ids || [];
  const setSuggested = (list) => upd({ suggested_program_ids: list, modules_count: list.length });
  const progMap = Object.fromEntries((programs || []).map((p) => [p.id, p]));
  return (
    <>
      <CheckList label={<>Ejes temáticos {AI_BADGE}</>} vals={c.thematic_areas || []} on={(vals) => upd({ thematic_areas: vals })} options={THEMATIC_OPTS} testid="cont-thematic" />
      <Text label={<>Nombre del training {AI_BADGE}</>} val={c.training_name || ""} on={(v) => upd({ training_name: v })} testid="cont-name" />
      <Textarea label={<>Objetivo {AI_BADGE}</>} val={c.objective || ""} on={(v) => upd({ objective: v })} rows={3} testid="cont-objective" />

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">Módulos seleccionados {AI_BADGE} ({suggested.length})</p>
        <ul className="mt-2 space-y-1 max-h-60 overflow-y-auto pr-1" data-testid="cont-modules">
          {suggested.map((m, i) => {
            const p = progMap[m.program_id];
            if (!p) return null;
            return (
              <li key={m.program_id} className="p-2 bg-[#F26A21]/5 border border-[#F26A21]/30 rounded flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-[#0B0B0B] truncate">
                    <span className="font-mono text-xs text-gray-500 mr-2">{p.code}</span>{p.title}
                  </p>
                  <p className="text-[11px] text-gray-600 italic">{m.reasoning}</p>
                </div>
                <button type="button" onClick={() => setSuggested(suggested.filter((_, j) => j !== i))}
                  data-testid={`cont-mod-remove-${p.code}`}
                  aria-label="Quitar módulo" className="text-xs text-red-500 hover:text-red-700">Quitar</button>
              </li>
            );
          })}
          {suggested.length === 0 && <li className="text-xs text-gray-500 italic">Sin módulos aún. Regenera propuesta o agrega manualmente en el catálogo.</li>}
        </ul>
      </div>

      <Textarea label={<>Perfil del egresado {AI_BADGE}</>} val={c.graduate_profile || ""} on={(v) => upd({ graduate_profile: v })} rows={3} testid="cont-graduate" />
      <CheckList label={<>Materiales didácticos {AI_BADGE}</>} vals={c.materials || []} on={(vals) => upd({ materials: vals })} options={MATERIALS_OPTS} testid="cont-materials" />
      <Textarea label={<>Bibliografía sugerida {AI_BADGE}</>} val={c.bibliography || ""} on={(v) => upd({ bibliography: v })} rows={4} testid="cont-biblio" />
      <CheckList label={<>Recursos didácticos {AI_BADGE}</>} vals={c.didactic_resources || []} on={(vals) => upd({ didactic_resources: vals })} options={RESOURCES_OPTS} testid="cont-resources" />
      <CheckList label={<>Metodología de evaluación {AI_BADGE}</>} vals={c.evaluation || []} on={(vals) => upd({ evaluation: vals })} options={EVALUATION_OPTS} testid="cont-eval" />
    </>
  );
}

function OperationsSection({ brief, setBrief }) {
  const o = brief.operations || {};
  const upd = (patch) => setBrief({ ...brief, operations: { ...o, ...patch } });
  const rc = o.requires_confirmation || {};
  const setRc = (patch) => upd({ requires_confirmation: { ...rc, ...patch } });
  const opts = ["Sí", "No", "Parcial", "N/A"];
  return (
    <>
      <Radio label={<>LMS actual del cliente {CONFIRM_BADGE}</>} val={rc.current_lms || ""} on={(v) => setRc({ current_lms: v })} options={opts} testid="ops-lms" />
      <Radio label={<>Personal de training interno {CONFIRM_BADGE}</>} val={rc.internal_training_staff || ""} on={(v) => setRc({ internal_training_staff: v })} options={opts} testid="ops-staff" />
      <Radio label={<>Equipo para diseñar promoción {CONFIRM_BADGE}</>} val={rc.team_for_promotion || ""} on={(v) => setRc({ team_for_promotion: v })} options={opts} testid="ops-promo" />
      <Radio label={<>Equipo para gestionar inscripciones {CONFIRM_BADGE}</>} val={rc.team_for_enrollment || ""} on={(v) => setRc({ team_for_enrollment: v })} options={opts} testid="ops-enroll" />
      <Radio label={<>Equipo para coordinar el training {CONFIRM_BADGE}</>} val={rc.team_for_coordination || ""} on={(v) => setRc({ team_for_coordination: v })} options={opts} testid="ops-coord" />
      <CheckList label={<>Requerimientos técnicos {AI_BADGE}</>} vals={o.technical_requirements || []} on={(vals) => upd({ technical_requirements: vals })} options={TECH_OPTS} testid="ops-tech" />
      <CheckList label={<>Canales de comunicación {AI_BADGE}</>} vals={o.communication_channels || []} on={(vals) => upd({ communication_channels: vals })} options={CHANNEL_OPTS} testid="ops-channels" />
      <CheckList label={<>Materiales de promoción {AI_BADGE}</>} vals={o.promotion_materials || []} on={(vals) => upd({ promotion_materials: vals })} options={PROMO_OPTS} testid="ops-promo-mats" />
    </>
  );
}

// ---------- ATOMS ----------
function Text({ label, val, on, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}
function TextNum({ label, val, on, testid }) {
  return (<label className="block"><span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
    <input type="number" min={0} value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" /></label>);
}
function TextDate({ label, val, on, testid }) {
  return (<label className="block"><span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
    <input type="date" value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" /></label>);
}
function Textarea({ label, val, on, testid, rows = 3 }) {
  return (<label className="block"><span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
    <textarea rows={rows} value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" /></label>);
}
function SelectRow({ label, val, on, options, testid }) {
  return (<label className="block"><span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
    <select value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]">
      <option value="">Selecciona...</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select></label>);
}
function Radio({ label, val, on, options, testid }) {
  return (<div><p className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</p>
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => on(o)} aria-pressed={val === o}
          data-testid={`${testid}-${o}`}
          className={`text-xs px-2.5 py-1 rounded border ${val === o ? "bg-[#0B0B0B] border-[#0B0B0B] text-white" : "bg-white border-gray-300 text-[#0B0B0B] hover:border-[#F26A21]"}`}>
          {o}
        </button>
      ))}
    </div></div>);
}
function CheckList({ label, vals, on, options, testid }) {
  const toggle = (o) => vals.includes(o) ? on(vals.filter((x) => x !== o)) : on([...vals, o]);
  return (<div><p className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</p>
    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const active = vals.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} aria-pressed={active}
            data-testid={`${testid}-${o}`}
            className={`text-xs px-2.5 py-1 rounded border ${active ? "bg-[#F26A21] border-[#F26A21] text-[#0B0B0B]" : "bg-white border-gray-300 text-[#0B0B0B] hover:border-[#F26A21]"}`}>
            {o}
          </button>
        );
      })}
    </div></div>);
}
